import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox, PhoneCall, Loader2, CheckCircle2, Users, Download } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { InsightsPanel } from "@/components/admin/InsightsPanel";
import { NewsletterPanel } from "@/components/admin/NewsletterPanel";
import { API, authHeaders } from "@/lib/api";

const statuses = ["new", "contacted", "in-progress", "closed"];
const statusColor = {
  new: "text-gold border-gold/40 bg-gold/10",
  contacted: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  "in-progress": "text-amber-300 border-amber-400/30 bg-amber-400/10",
  closed: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
};
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("leads");
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem("arth_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: authHeaders() });
      setLeads(res.data.leads);
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("arth_token")) return navigate("/admin/login");
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Delete failed");
    }
  };

  const saveNotes = async (id, notes) => {
    const lead = leads.find((l) => l.id === id);
    if ((lead?.notes || "") === notes) return;
    try {
      await axios.patch(`${API}/leads/${id}`, { notes }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes } : l)));
      toast.success("Note saved");
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Could not save note");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "globus-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Export failed");
    }
  };

  const visible = useMemo(
    () => (filter === "all" ? leads : leads.filter((l) => l.status === filter)),
    [leads, filter]
  );

  const counts = useMemo(() => {
    const c = { total: leads.length, new: 0, contacted: 0, "in-progress": 0, closed: 0 };
    leads.forEach((l) => { if (c[l.status] !== undefined) c[l.status] += 1; });
    return c;
  }, [leads]);

  const statCards = [
    { label: "Total leads", value: counts.total, icon: Users },
    { label: "New", value: counts.new, icon: Inbox },
    { label: "In progress", value: counts["in-progress"] + counts.contacted, icon: PhoneCall },
    { label: "Closed", value: counts.closed, icon: CheckCircle2 },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-night text-foreground">
      <header className="border-b border-white/10 bg-night/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md px-3 py-1.5 flex items-center shadow-md shadow-black/30 shrink-0">
              <img src="/globus-lockup.png" alt="Globus Financial Services" className="h-7 w-auto object-contain" />
            </span>
            <span className="text-[9px] tracking-[0.28em] uppercase text-muted-foreground">Advisor Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="border border-gold text-gold hover:bg-gold/10 transition-colors rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-2"
            >
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="refresh-leads-button" onClick={load} className="border border-white/10 hover:border-gold/50 transition-colors rounded-sm p-2.5 text-[#A0AAB2] hover:text-gold" aria-label="Refresh">
              <RefreshCw size={16} />
            </button>
            <Link data-testid="view-site-link" to="/" className="text-sm text-[#A0AAB2] hover:text-gold transition-colors hidden sm:block">View site</Link>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="border border-gold text-gold hover:bg-gold/10 transition-colors rounded-sm px-4 py-2 text-sm font-medium flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-2 mb-10" data-testid="dashboard-view-toggle">
          {["leads", "insights", "newsletter"].map((v) => (
            <button
              key={v}
              data-testid={`view-${v}`}
              onClick={() => setView(v)}
              className={`text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm border transition-colors ${
                view === v ? "border-gold text-gold bg-gold/10" : "border-white/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
          {statCards.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-slate2 border border-white/10 p-6">
              <s.icon size={18} className="text-gold mb-4" />
              <p className="font-serif text-3xl">{s.value}</p>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {view === "leads" && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h1 className="font-serif text-2xl sm:text-3xl">Enquiries</h1>
          <div className="flex gap-2 flex-wrap" data-testid="status-filters">
            {["all", ...statuses].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-xs uppercase tracking-widest px-4 py-2 rounded-sm border transition-colors ${
                  filter === s ? "border-gold text-gold bg-gold/10" : "border-white/10 text-muted-foreground hover:text-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        )}

        {view === "newsletter" ? (
          <NewsletterPanel onAuthError={logout} />
        ) : view === "insights" ? (
          <InsightsPanel onAuthError={logout} />
        ) : loading ? (
          <div className="flex justify-center py-24" data-testid="leads-loading"><Loader2 className="animate-spin text-gold" size={28} /></div>
        ) : visible.length === 0 ? (
          <div className="bg-slate2 border border-white/10 py-24 text-center" data-testid="leads-empty">
            <Inbox size={28} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm">No enquiries here yet.</p>
          </div>
        ) : (
          <div className="bg-slate2 border border-white/10 overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10">
                  <th className="px-6 py-4 font-medium">Client</th>
                  <th className="px-6 py-4 font-medium">Contact</th>
                  <th className="px-6 py-4 font-medium">Loan</th>
                  <th className="px-6 py-4 font-medium">Amount</th>
                  <th className="px-6 py-4 font-medium">City</th>
                  <th className="px-6 py-4 font-medium">Received</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium" />
                </tr>
              </thead>
              <tbody>
                {visible.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors align-top">
                    <td className="px-6 py-5">
                      <p className="font-medium text-foreground">{lead.name}</p>
                      {lead.message && <p className="text-xs text-muted-foreground mt-1 max-w-[240px] line-clamp-2">{lead.message}</p>}
                      <textarea
                        data-testid={`lead-notes-${lead.id}`}
                        defaultValue={lead.notes || ""}
                        onBlur={(e) => saveNotes(lead.id, e.target.value)}
                        placeholder="Add call notes…"
                        rows={2}
                        className="mt-2 w-full max-w-[240px] bg-slate3 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold transition-colors"
                      />
                    </td>
                    <td className="px-6 py-5 text-[#A0AAB2]">
                      <p>{lead.phone}</p>
                      <p className="text-xs text-muted-foreground mt-1">{lead.email}</p>
                    </td>
                    <td className="px-6 py-5 text-[#A0AAB2]">{lead.loan_type}</td>
                    <td className="px-6 py-5 font-medium text-foreground whitespace-nowrap">{inr(lead.amount)}</td>
                    <td className="px-6 py-5 text-[#A0AAB2]">{lead.city}</td>
                    <td className="px-6 py-5 text-muted-foreground whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-6 py-5">
                      <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                        <SelectTrigger data-testid={`lead-status-${lead.id}`} className={`rounded-sm border text-xs uppercase tracking-wider h-9 w-[140px] ${statusColor[lead.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate2 border-white/10">
                          {statuses.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`} className="uppercase text-xs tracking-wider">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-5">
                      <button
                        data-testid={`lead-delete-${lead.id}`}
                        onClick={() => remove(lead.id)}
                        className="text-muted-foreground hover:text-red-400 transition-colors"
                        aria-label="Delete lead"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
